<script>
import draggable from 'vuedraggable'
import { SIDEBAR_CHAT } from '@modules/aawz-intelligence-v2/constants/TextChat'
import IaInsightCard from '@modules/aawz-intelligence-v2/components/ia-insight-card/ia-insight-card.vue'

// Espelha o `gap` do `.personalizacao-canvas__lista` (variável SASS não
// atravessa para o script) — entra na conta de `larguraDoItem`.
const GAP_DA_LISTA_PX = 6

// Cartões `compacto` dividem a linha (hoje sempre 3 — Compliance/Hub/
// Partnership/Backoffice); os demais ocupam a linha inteira. Aproxima o
// agrupamento real (`utils/insights.js::agruparEmLinhas`) com um único
// container flex-wrap, que é o que o vuedraggable precisa para reordenar um
// array só. O desconto do `gap` bate com `GAP_DA_LISTA_PX`, e o
// `flex-grow: 1` absorve o arredondamento — a linha fecha na largura do card
// cheio.
function larguraDoItem(item) {
  const colunas = Number(item && item.porLinha) || 1
  if (colunas <= 1) return '100%'
  const desconto = (GAP_DA_LISTA_PX * (colunas - 1)) / colunas
  return `calc(${100 / colunas}% - ${desconto}px)`
}

// Só os props que `IaInsightCard` conhece — `item` também carrega
// `id`/`moduleKey`/`routeName`/`porLinha`/... que não são dele.
function propsDoCard(item) {
  return {
    variante: item.variante || 'compacto',
    rotulo: item.rotuloCompleto || item.rotulo,
    valor: item.valor,
    unidade: item.unidade,
    destaque: item.destaque,
    tomDestaque: item.tomDestaque,
    legenda: item.legenda,
    segmentos: item.segmentos,
    escala: item.escala,
    serie: item.serie,
    progresso: item.progresso,
    pontos: item.pontos,
    barras: item.barras,
    disponivel: item.disponivel !== false,
    motivo: item.motivo,
  }
}

// Módulos VISÍVEIS do painel de insights, reordenável — o canvas do editor.
// ★ O card aqui é o MESMO `IaInsightCard` real (mesma altura/estrutura de
// produção, pedido do usuário), com o DADO REAL de quem já tem fonte — o
// `item` chega da view já mesclado com os números do endpoint
// (`itensDoCatalogoComIcone`, `mesclarInsights`). Quem ainda não tem fonte
// mostra "Em breve" (`disponivel: false`), a MESMA verdade que a lateral
// normal mostra — nunca um número inventado (`dadosProvisorios` foi
// removido do repo de propósito, 03/09/2026). Cada card ganha uma moldura
// pontilhada (a superfície já é a mesma do fundo — `.ia-insight` nasce
// `background: transparent`, não precisou de override) e um toolbar no
// canto (alça de arrastar + ocultar), no molde do protótipo do usuário.
export default {
  name: 'PersonalizacaoCanvas',
  components: {
    draggable,
    IaInsightCard,
  },
  props: {
    // `[{id, rotulo, rotuloCompleto, icone, variante, porLinha}]` — os
    // módulos visíveis, na ordem atual.
    itens: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      itensLocais: [],
    }
  },
  computed: {
    texto() {
      return SIDEBAR_CHAT
    },
    // Estilo e props do card pré-computados: o template não chama função
    // (o drag re-renderiza a cada quadro). `itensLocais` segue sendo o
    // `v-model` do draggable; esta lista só é lida pelo `v-for`.
    entradas() {
      return this.itensLocais.map((item) => ({
        item,
        compacto: item.variante === 'compacto',
        estilo: { flex: `1 1 ${larguraDoItem(item)}` },
        props: propsDoCard(item),
      }))
    },
  },
  watch: {
    itens: {
      immediate: true,
      handler(next) {
        this.itensLocais = (next || []).slice()
      },
    },
  },
  methods: {
    onChange() {
      this.$emit(
        'reordenar',
        this.itensLocais.map((item) => item.id)
      )
    },
    remover(id) {
      this.$emit('remover', id)
    },
  },
}
</script>

<template>
  <div class="personalizacao-canvas">
    <!-- ★ `<draggable>` só tem os itens DE VERDADE — nunca um elemento a
         mais. O vuedraggable espera 1:1 entre `v-model` e os filhos do slot
         default; a mensagem de vazio era um filho A MAIS (sem contraparte no
         array), e o Sortable a tratava como parte da área ordenável — o
         placeholder do drag em voo era inserido DENTRO do texto dela (o
         ícone aparecia colado no meio de "Arraste um módulo [ícone] abaixo
         pra cá", achado do usuário ao vivo). Com a lista vazia,
         `<draggable>` continua sendo um elemento real na árvore, do tamanho
         de `min-height` — é ELE o alvo do drop, sempre, item nenhum
         necessário. A mensagem de vazio é IRMÃ dele (`v-if`, posicionada por
         cima com `position: absolute`), nunca filha — não interfere na
         contagem nem na área de drop. -->
    <draggable
      v-model="itensLocais"
      :group="{ name: 'painel-insights', pull: true, put: true }"
      :animation="200"
      ghost-class="personalizacao-canvas__ghost"
      class="personalizacao-canvas__lista"
      data-test="personalizacao-canvas-area"
      @change="onChange"
    >
      <div
        v-for="entrada in entradas"
        :key="entrada.item.id"
        class="personalizacao-canvas__item"
        :class="{ 'personalizacao-canvas__item--compacto': entrada.compacto }"
        :style="entrada.estilo"
        data-test="personalizacao-canvas-item"
      >
        <div class="personalizacao-canvas__toolbar">
          <icon-svg
            name="mdi-drag-vertical"
            color="var(--muted)"
            size="14"
            :aria-label="texto.personalizacaoArrastar"
            :title="texto.personalizacaoArrastar"
          />
          <v-btn
            icon
            x-small
            :aria-label="texto.personalizacaoOcultar"
            :title="texto.personalizacaoOcultar"
            @click.stop="remover(entrada.item.id)"
          >
            <icon-svg name="eye-close" :size="14" color="muted" />
          </v-btn>
        </div>
        <IaInsightCard v-bind="entrada.props" />
      </div>
    </draggable>
    <div
      v-if="!itensLocais.length"
      class="personalizacao-canvas__vazio ia-sidebar-aux"
      data-test="personalizacao-canvas-vazio"
    >
      {{ texto.personalizacaoCanvasVazio }}
    </div>
  </div>
</template>

<style lang="sass" scoped>
.personalizacao-canvas
  position: relative
  display: flex
  flex-direction: column
  // 140px, não 60: com o canvas vazio (tudo oculto) o alvo do drop
  // precisa ser generoso — 60px era um alvo estreito demais pra soltar o
  // módulo ali dentro (achado do usuário ao vivo).
  min-height: 140px

  // `flex: 1` pra o alvo do drop acompanhar o canvas quando a lateral o
  // estica (modo edição, `chat-sidebar.vue`): soltar em QUALQUER ponto do
  // painel vazio funciona, não só nos 140px do topo.
  &__lista
    display: flex
    flex-wrap: wrap
    align-content: flex-start
    gap: 6px
    flex: 1
    min-height: inherit

  &__item
    position: relative
    border: 1px dashed var(--neutral-300)
    border-radius: var(--radius-sm)
    // Horizontal só: a variante cheia (hero/escala/serie/progresso) já traz
    // `padding: 14px 0` própria do `IaInsightCard` — padding vertical aqui
    // dobraria o respiro. A `compacto` zera o próprio (ver o `&--compacto`
    // abaixo) porque normalmente é a LINHA da lateral real quem dá esse
    // respiro; aqui não há linha, e sem compensar ela colava no topo/base
    // da caixa pontilhada.
    padding: 0 12px
    cursor: grab

    &:active
      cursor: grabbing

  &__item--compacto
    padding: 10px 12px

  &__toolbar
    position: absolute
    top: 6px
    right: 6px
    display: flex
    align-items: center
    gap: 2px
    z-index: 1

  // Por cima do `<draggable>` (que fica visível por baixo — é ele o alvo
  // REAL do drop, do tamanho de `min-height`), nunca dentro dele: irmã, não
  // filha (ver o comentário no template). `pointer-events: none` deixa o
  // clique/drop atravessar direto para o container de verdade.
  &__vazio
    position: absolute
    inset: 0
    display: flex
    align-items: center
    justify-content: center
    text-align: center
    padding: 16px 8px
    pointer-events: none

  &__ghost
    opacity: 0.4
</style>
